import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Typography,
  CircularProgress,
  styled,
  IconButton,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import AddRoomModal from "./AddRoomModal";
import DeleteRoomModal from "./DeleteRoomModal";
import PaginationComponent from "./PaginationComponent";
import RoomAnalytics from "./RoomAnalytics";
import styles from "./styles";
import { getApiData } from "../../../../utils/APIHelper";
import { Setting } from "../../../../utils/Setting";
import { getRoomAnalytics } from "../../../../services/budgetService";

const RoomsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "repeat(1, minmax(0, 1fr))", // 1 column on xs (mobile)
  [theme.breakpoints.up("sm")]: {
    gap: "14px",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))", // 2 columns on small
  },
  [theme.breakpoints.up("md")]: {
    gap: "16px",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))", // 3 columns on tablet
  },
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))", // max 4 columns on desktop+
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "none",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "800",
  borderRadius: "10px",
  padding: ".5vh .7vw",
  letterSpacing: "0",
}));

const Rooms = ({ details, isMobile, passedStyles }) => {
  const navigate = useNavigate();
  const roomStyles = styles(isMobile);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);
  const [isDeleteRoomModalOpen, setIsDeleteRoomModalOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [roomToEdit, setRoomToEdit] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    perPage: 10,
    prevEnable: false,
    nextEnable: false,
  });

  // Fetch rooms from API
  const fetchRooms = async (page = 1) => {
    if (!details?.id) return;

    setLoading(true);
    try {
      const response = await getApiData(
        `api/project/${details.id}/rooms`,
        "GET",
        {}
      );

      if (response.success && response.data) {
        setRooms(response.data);
        setPagination({
          currentPage: response.page || 1,
          totalPages: response.total_pages || 1,
          totalCount: response.total_count || 0,
          perPage: response.per_page || 10,
          prevEnable: response.prev_enable || false,
          nextEnable: response.next_enable || false,
        });
      } else {
        // Handle API error response
        console.error(
          "API Error:",
          response.message || "Failed to fetch rooms"
        );
        setRooms([]);
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
      setRooms([]);
      // You could add a toast notification here for user feedback
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    if (!details?.id) return;
    setAnalyticsLoading(true);
    try {
      const response = await getRoomAnalytics(details.id);
      if (response?.success) {
        setAnalytics(response.analytics || response.data || response);
      }
    } catch (error) {
      console.error("Error fetching room analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchRooms(page);
  };

  // Handle add room
  const handleAddRoom = (newRoom) => {
    // After adding a room, refresh the current page
    fetchRooms(pagination.currentPage);
    fetchAnalytics();
    setIsAddRoomModalOpen(false);
    setRoomToEdit(null);
  };

  // Handle edit room
  const handleEditRoom = (room, e) => {
    if (e) {
      e.stopPropagation();
    }
    setRoomToEdit(room);
    setIsAddRoomModalOpen(true);
  };

  // Handle delete room
  const handleDeleteRoom = (room, e) => {
    if (e) {
      e.stopPropagation();
    }
    setRoomToDelete(room);
    setIsDeleteRoomModalOpen(true);
  };

  // Handle close delete modal
  const handleCloseDeleteModal = () => {
    setIsDeleteRoomModalOpen(false);
    setRoomToDelete(null);
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setIsAddRoomModalOpen(false);
    setRoomToEdit(null);
  };

  // Handle delete success
  const handleDeleteSuccess = () => {
    fetchRooms(pagination.currentPage);
    fetchAnalytics();
  };

  // Handle room card click
  const handleRoomClick = (room) => {
    navigate(`/project/details/${details.id}/room/${room.room_id}`, {
      state: {
        roomData: room,
        projectDetails: details,
        passedStyles: passedStyles,
      },
    });
  };

  // Load rooms on component mount
  useEffect(() => {
    fetchRooms();
    fetchAnalytics();
  }, [details?.id]);

  return (
    <div style={roomStyles.container}>
      {/* Header Section */}
      <Box sx={roomStyles.header}>
        <Box>
          <div style={roomStyles.title}>Rooms</div>
        </Box>

        {/* Add Room CTA hidden — Rooms tab is read-only */}
      </Box>

      <RoomAnalytics
        analytics={analytics}
        isLoading={analyticsLoading}
        isMobile={isMobile}
      />

      {/* Rooms Grid */}
      {loading ? (
        <Box sx={roomStyles.loadingContainer}>
          <CircularProgress size={40} />
        </Box>
      ) : rooms.length === 0 ? (
        <center style={{ padding: "2vmax" }}>No rooms found</center>
      ) : (
        <RoomsGrid>
          {rooms.map((room) => (
            <div
              key={room.room_id}
              style={roomStyles.roomCard}
              onClick={() => handleRoomClick(room)}
              className="room-card-clickable"
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                }}
              >
                <CardMedia
                  component="img"
                  image={
                    room.room_image_url ||
                    "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                  }
                  alt={room.room_name}
                  sx={roomStyles.cardImage}
                  onError={(e) => {
                    e.target.src = "/api/placeholder/300/200";
                  }}
                />
                {/* Edit/Delete overlay hidden — Rooms tab is read-only */}
              </Box>
              <CardContent sx={roomStyles.cardContent}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "15px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "1vh",
                  }}
                >
                  <div style={roomStyles.roomName}>{room.room_name}</div>

                  <Chip
                    label={room.room_type}
                    size="small"
                    sx={{
                      backgroundColor: "#e3f2fd",
                      color: "#4d6bf6",
                      fontWeight: 600,
                      fontSize: "12px",
                      borderRadius: "20px",
                      padding: ".5vh .5vw",
                    }}
                  />
                </div>

                {/* Room Details */}
                <div style={roomStyles.detailsSection}>
                  <Typography variant="body2" sx={roomStyles.roomDetail}>
                    Area: {room.area}m²
                  </Typography>
                  <Typography variant="body2" sx={roomStyles.roomDetail}>
                    Height: {room.height}m
                  </Typography>
                  <Typography variant="body2" sx={roomStyles.roomDetail}>
                    Inclusions: {room.inclusion_count}
                  </Typography>
                </div>

                {/* Style and Palette */}
                <Box sx={roomStyles.styleSection}>
                  <Typography variant="body2" sx={roomStyles.roomDetail}>
                    <strong>Style:</strong> {room.style}
                  </Typography>
                  <Typography variant="body2" sx={roomStyles.roomDetail}>
                    <strong>Palette:</strong> {room.palette}
                  </Typography>
                </Box>
              </CardContent>
            </div>
          ))}
        </RoomsGrid>
      )}

      {/* Pagination */}
      {!loading && rooms.length > 0 && (
        <PaginationComponent
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          perPage={pagination.perPage}
          onPageChange={handlePageChange}
          loading={loading}
          prevEnable={pagination.prevEnable}
          nextEnable={pagination.nextEnable}
        />
      )}

      {/* Add/Edit Room Modal */}
      <AddRoomModal
        open={isAddRoomModalOpen}
        onClose={handleCloseEditModal}
        onAddRoom={handleAddRoom}
        projectDetails={details}
        editRoom={roomToEdit}
      />

      {/* Delete Room Modal */}
      <DeleteRoomModal
        open={isDeleteRoomModalOpen}
        onClose={handleCloseDeleteModal}
        roomData={roomToDelete}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default Rooms;
